import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Customer-facing read of the packaging grid (Stage 2 Phase C).
 *
 * The Plans page renders from the SAME tables the entitlement resolver enforces, so the
 * published comparison chart can never drift from what the code actually allows. This hook
 * is presentation only — it never decides access. Access answers come from useEntitlements.
 */

export type CatalogPlan = {
  /** Surrogate identity. Match on this, never on `key` or `name`. */
  id: string;
  key: string;
  name: string;
  tagline: string;
  best_for: string;
  /** null = price not published yet, 0 = free. */
  price_cents: number | null;
  currency: string;
  billing_interval: string;
  badge: string | null;
  is_public: boolean;
  /** Lifecycle of the plan. The catalog only lists `active` plans plus the plan you hold. */
  lifecycle?: 'draft' | 'active' | 'hidden' | 'archived';
  /** Ladder position, set from the admin console — never hardcoded in the UI. */
  sort_order?: number;
};

export type CatalogFeature = {
  key: string;
  label: string;
  description: string;
  feature_type: 'boolean' | 'limit' | 'quota' | string;
  unit: string | null;
  status: 'live' | 'coming_soon' | 'retired' | string;
  personas: string[];
  /** Customer-facing section title for the comparison chart. Operator-authored, may be unset. */
  group_label?: string | null;
};

export type CatalogCell = {
  plan_id: string;
  /** Read convenience only — derived from plan_id by a DB trigger. */
  plan_key: string;
  feature_key: string;
  enabled: boolean;
  limit_value: number | null;
  quota_value: number | null;
  quota_period: string | null;
  display_text: string | null;
};

/**
 * A row on the customer-facing comparison chart. Presentation only — these rows never decide
 * access. When `capability_key` is set, the row describes a capability the app really enforces
 * and its ticks are resolved against the published grid, so it cannot overstate access.
 */
export type MarketingFeature = {
  key: string;
  label: string;
  description: string;
  group_label: string;
  group_sort: number;
  sort_order: number;
  status: 'live' | 'coming_soon' | 'retired' | string;
  capability_key: string | null;
};

export type MarketingCell = {
  plan_id: string;
  feature_key: string;
  enabled: boolean;
  display_text: string | null;
};

export type PlanCatalog = {
  revision: number | null;
  plans: CatalogPlan[];
  features: CatalogFeature[];
  cells: CatalogCell[];
  marketing_features?: MarketingFeature[];
  marketing_cells?: MarketingCell[];
  /** The one plan id the signed-in company holds (null before enrolment resolves, always null publicly). */
  my_plan_id: string | null;
  /** Public read only: true while nobody can be charged. Signed-in surfaces use useLaunchOffer. */
  launch_active?: boolean;
};

const empty: PlanCatalog = { revision: null, plans: [], features: [], cells: [], my_plan_id: null };


/** Cell text for the comparison matrix. Prefers the admin-authored copy, never shows raw keys. */
export function cellLabel(cell: CatalogCell | undefined): string {
  if (!cell || !cell.enabled) return '—';
  if (cell.display_text) return cell.display_text;
  if (cell.quota_value != null) {
    const period = cell.quota_period === 'month' ? 'mo' : cell.quota_period ?? 'period';
    return `${cell.quota_value} / ${period}`;
  }
  if (cell.limit_value != null) return `Up to ${cell.limit_value}`;
  return 'Included';
}

export function formatPlanPrice(plan: CatalogPlan): { amount: string; suffix: string | null } {
  if (plan.price_cents == null) return { amount: 'Coming soon', suffix: null };
  if (plan.price_cents === 0) return { amount: 'FREE', suffix: null };
  const dollars = plan.price_cents / 100;
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: plan.currency || 'USD',
    maximumFractionDigits: dollars % 1 === 0 ? 0 : 2,
  }).format(dollars);
  return { amount, suffix: `/ ${plan.billing_interval}` };
}

/**
 * The few lines that go on a plan card. Differentiators first (things not every plan
 * has), then shared essentials, so the cards explain why one plan costs more.
 * Always the admin-authored label — never a raw feature key.
 */
export function planHighlights(catalog: PlanCatalog, planId: string, max = 4): string[] {
  const enabledOn = (pid: string, key: string) =>
    catalog.cells.some((c) => c.plan_id === pid && c.feature_key === key && c.enabled);

  const mine = catalog.features.filter((f) => f.status !== 'coming_soon' && enabledOn(planId, f.key));

  const score = (key: string) => {
    const grantedTo = catalog.plans.filter((p) => enabledOn(p.id, key)).length;
    const distinctValues = new Set(
      catalog.cells
        .filter((c) => c.feature_key === key && c.enabled)
        .map((c) => c.display_text ?? c.limit_value ?? c.quota_value ?? 'on'),
    ).size;
    // Fewer plans granting it, or more variation in the value, ranks higher.
    return grantedTo * 10 - distinctValues;
  };

  return mine
    .sort((a, b) => score(a.key) - score(b.key))
    .slice(0, max)
    .map((f) => {
      const cell = catalog.cells.find((c) => c.plan_id === planId && c.feature_key === f.key);
      const detail = cell?.display_text ?? (cell?.limit_value != null ? `up to ${cell.limit_value}` : null);
      return detail ? `${f.label} — ${detail}` : f.label;
    });
}

/**
 * Which side of the ladder a plan sits on, read from the published grid — never from the
 * plan's name. A plan that grants a capability no buyer persona has (inventory, menus,
 * insights) is a selling plan; everything else is a buying plan.
 */
export function planSide(catalog: PlanCatalog, planId: string): 'buying' | 'selling' {
  const sellerOnlyKeys = catalog.features
    .filter((f) => f.personas?.length && !f.personas.includes('buyer'))
    .map((f) => f.key);
  const grantsSellerOnly = catalog.cells.some(
    (c) => c.plan_id === planId && c.enabled && sellerOnlyKeys.includes(c.feature_key),
  );
  return grantsSellerOnly ? 'selling' : 'buying';
}

export type BundleSaving = {
  /** Saving per billing period, in cents. */
  cents: number;
  /** The plan names the saving is measured against — named in the copy, never hardcoded. */
  againstPlanNames: string[];
};

/**
 * What a combined plan saves against buying one plan per side.
 * Returns null unless the plan really covers both sides AND the maths is positive, so the
 * page can never claim a saving that the live prices do not support.
 */
export function bundleSaving(catalog: PlanCatalog, planId: string): BundleSaving | null {
  const plan = catalog.plans.find((p) => p.id === planId);
  if (!plan || plan.price_cents == null || plan.price_cents <= 0) return null;

  const enabledCells = (pid: string) => catalog.cells.filter((c) => c.plan_id === pid && c.enabled);
  const mine = new Map(enabledCells(planId).map((c) => [c.feature_key, c]));

  /* A capability only counts as covered when this plan is at least as generous on it —
     an allowance of 3 does not replace an unlimited allowance. */
  const atLeastAsGenerous = (theirs: CatalogCell) => {
    const ours = mine.get(theirs.feature_key);
    if (!ours) return false;
    const covers = (o: number | null, t: number | null) =>
      t == null ? o == null : o == null || o >= t;
    return covers(ours.limit_value, theirs.limit_value) && covers(ours.quota_value, theirs.quota_value);
  };

  /* Cheapest priced plan per side whose capabilities this plan fully covers. */
  const cheapestCovered = (side: 'buying' | 'selling') => {
    const candidates = catalog.plans.filter(
      (p) =>
        p.id !== planId &&
        p.price_cents != null &&
        p.price_cents > 0 &&
        planSide(catalog, p.id) === side &&
        enabledCells(p.id).every(atLeastAsGenerous),
    );
    if (candidates.length === 0) return null;
    return candidates.reduce((a, b) => ((a.price_cents ?? 0) <= (b.price_cents ?? 0) ? a : b));
  };

  const buying = cheapestCovered('buying');
  const selling = cheapestCovered('selling');
  if (!buying || !selling) return null;

  const saving = (buying.price_cents ?? 0) + (selling.price_cents ?? 0) - plan.price_cents;
  if (saving <= 0) return null;
  return { cents: saving, againstPlanNames: [buying.name, selling.name] };
}

/** Features in section order, preserving catalog sort order inside each section. */
export function groupedFeatures(
  features: CatalogFeature[],
): { label: string | null; features: CatalogFeature[] }[] {
  const groups: { label: string | null; features: CatalogFeature[] }[] = [];
  for (const feature of features) {
    const label = feature.group_label?.trim() || null;
    const existing = groups.find((g) => g.label === label);
    if (existing) existing.features.push(feature);
    else groups.push({ label, features: [feature] });
  }
  // A single unlabelled bucket means no sections were authored — render one flat list.
  if (groups.length === 1 && groups[0].label === null) return groups;
  return groups;
}

export type ComparisonValue = { included: boolean; text: string | null };

export type ComparisonRow = {
  key: string;
  label: string;
  description: string;
  comingSoon: boolean;
  values: Record<string, ComparisonValue>;
};

export type ComparisonSection = { label: string; rows: ComparisonRow[] };

/**
 * The full customer-facing comparison, section by section.
 *
 * A row tied to an enforced capability takes its yes/no straight from the published grid, so
 * the chart can never claim access the app blocks — the marketing row only supplies wording.
 * Rows with no capability behind them (platform guarantees, support, team management) are
 * rendered from the marketing values alone.
 */
export function comparisonSections(catalog: PlanCatalog): ComparisonSection[] {
  const rows = (catalog.marketing_features ?? []).filter((f) => f.status !== 'retired');
  const marketingCells = catalog.marketing_cells ?? [];
  const sections: ComparisonSection[] = [];

  const sorted = [...rows].sort(
    (a, b) => a.group_sort - b.group_sort || a.sort_order - b.sort_order,
  );

  for (const row of sorted) {
    const values: Record<string, ComparisonValue> = {};
    for (const plan of catalog.plans) {
      const mine = marketingCells.find(
        (c) => c.plan_id === plan.id && c.feature_key === row.key,
      );
      const enforced = row.capability_key
        ? catalog.cells.find(
            (c) => c.plan_id === plan.id && c.feature_key === row.capability_key,
          )
        : undefined;

      const included = row.capability_key
        ? !!mine?.enabled && !!enforced?.enabled
        : !!mine?.enabled;

      const text =
        mine?.display_text ??
        (enforced && enforced.display_text ? enforced.display_text : null);

      values[plan.id] = { included, text: included ? text : null };
    }

    const section =
      sections.find((s) => s.label === row.group_label) ??
      (sections.push({ label: row.group_label, rows: [] }), sections[sections.length - 1]);

    section.rows.push({
      key: row.key,
      label: row.label,
      description: row.description,
      comingSoon: row.status === 'coming_soon',
      values,
    });
  }

  return sections;
}

export function usePlanCatalog() {
  const { user } = useAuth();

  /* Signed-out visitors read the marketing-only catalog (`get_public_plan_catalog`):
     published public plans, the feature list and the grid — no company-scoped fields.
     Signed-in behaviour is unchanged, including `my_plan_id`. */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['plan-catalog', user?.id ?? 'public'],
    queryFn: async (): Promise<PlanCatalog> => {
      if (!user) {
        const { data, error } = await supabase.rpc('get_public_plan_catalog');
        if (error) throw error;
        const payload = (data as unknown as PlanCatalog) ?? empty;
        return { ...payload, my_plan_id: null };
      }
      const { data, error } = await supabase.rpc('get_plan_catalog');
      if (error) throw error;
      return (data as unknown as PlanCatalog) ?? empty;
    },
    staleTime: 5 * 60_000,
  });

  const catalog = data ?? empty;


  return {
    catalog,
    isLoading,
    isError: !!error,
    error,
    refetch,
    cellFor: (planId: string, featureKey: string) =>
      catalog.cells.find((c) => c.plan_id === planId && c.feature_key === featureKey),
  };
}
